import React from 'react'
import { SWRResponse, KeyedMutator } from 'swr'
import { useLogger } from '@tumelo/logging'

export function SWRUnwrapper<T>({
  response,
  loadingComponent,
  errorComponent,
  validatingComponent,
  children,
}: {
  response: SWRResponse<T>
  loadingComponent: React.ReactElement
  errorComponent: React.ReactElement
  validatingComponent?: React.ReactElement
  children: (t: T) => React.ReactNode
}) {
  const { data, error, isLoading, isValidating } = response

  const logger = useLogger()

  if (error) {
    logger.logError(error)
    return <>{errorComponent}</>
  }
  if (isValidating && !isLoading && validatingComponent) return <>{validatingComponent}</>
  if (isLoading || isValidating) return <>{loadingComponent}</>

  return <>{children(data as T)}</>
}

export const combineSWRResponses = <T extends unknown[]>(
  ...responses: { [K in keyof T]: SWRResponse<T[K]> }
): SWRResponse<T> => {
  return {
    data: responses.map((r) => r.data) as T,
    error: responses.some((r) => r.error),
    isLoading: responses.some((r) => r.isLoading),
    isValidating: responses.some((r) => r.isValidating),
    mutate: (() => {
      responses.every((r) => r.mutate())
    }) as KeyedMutator<T>,
  }
}
